import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, VStack, HStack, Text, Icon, Button, Input, Select,
  FormControl, FormLabel, FormHelperText,
  Spinner, Alert, AlertIcon, AlertDescription, IconButton, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, useDisclosure, useToast, Switch, Divider, SimpleGrid,
} from '@chakra-ui/react';
import {
  FiPlus, FiEdit3, FiTrash2, FiRefreshCw, FiMapPin,
  FiCheckCircle, FiXCircle, FiLogIn, FiLogOut, FiInfo,
  FiAlertTriangle, FiDatabase,
} from 'react-icons/fi';
import { listScanners, upsertScanner, deleteScanner, generateAllTokens } from 'services/qrService';

const MODES = [
  { value: 'access_log',   label: 'Registro de Entrada', icon: FiLogIn,  chipBg: '#DCF1E6', chipColor: '#1E7A4E', tileBg: '#DCF1E6', tileColor: '#1E7A4E' },
  { value: 'exit_log',     label: 'Registro de Salida',  icon: FiLogOut, chipBg: '#FCE7CC', chipColor: '#B5631A', tileBg: '#FCE7CC', tileColor: '#B5631A' },
  { value: 'info_only',    label: 'Solo Información',    icon: FiInfo,   chipBg: '#E2ECFB', chipColor: '#2A66B4', tileBg: '#E2ECFB', tileColor: '#2A66B4' },
  { value: 'event_ticket', label: 'Validación Entrada',  icon: FiCheckCircle, chipBg: '#EDE9F8', chipColor: '#6D28D9', tileBg: '#EDE9F8', tileColor: '#6D28D9' },
];

const EMPTY_FORM = { scanner_id: '', name: '', location: '', mode: 'access_log', active: true, scheduled_end_time: '' };

// ── Chip de modo ─────────────────────────────────────────────────────────────
const ModeChip = ({ mode }) => {
  const cfg = MODES.find(m => m.value === mode) ?? MODES[0];
  return (
    <Flex
      as="span" display="inline-flex" align="center" gap="5px"
      px="10px" py="4px" borderRadius="999px"
      bg={cfg.chipBg} color={cfg.chipColor}
      fontSize="12px" fontWeight="700" whiteSpace="nowrap"
    >
      <Icon as={cfg.icon} boxSize="13px" />
      {cfg.label}
    </Flex>
  );
};

// ── Botón de acción de fila ──────────────────────────────────────────────────
const ActBtn = ({ icon, label, danger, onClick, isLoading }) => (
  <Tooltip label={label}>
    <IconButton
      icon={<Icon as={icon} boxSize="15px" />}
      w="32px" h="32px" minW="32px" borderRadius="9px"
      bg="cream.bg2" border="none" color={danger ? 'danger' : 'ink.400'}
      _hover={{ bg: danger ? '#FEE2E2' : 'coral.soft', color: danger ? 'danger' : 'coral.700' }}
      onClick={onClick}
      isLoading={isLoading}
      aria-label={label}
    />
  </Tooltip>
);

const ScannerAdmin = () => {
  const [scanners, setScanners]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [deleting, setDeleting]         = useState(null);
  const [error, setError]               = useState(null);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [editing, setEditing]           = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [generatingTokens, setGeneratingTokens] = useState(false);
  const [tokenResult, setTokenResult]           = useState(null);

  const { isOpen: isFormOpen,   onOpen: onOpenForm,   onClose: onCloseForm   } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();

  const toast = useToast();

  const fetchScanners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listScanners();
      setScanners(res.data ?? []);
    } catch {
      setError('No se pudo cargar la lista de lectores.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchScanners(); }, [fetchScanners]);

  const handleGenerateAllTokens = async () => {
    setGeneratingTokens(true);
    setTokenResult(null);
    try {
      const res = await generateAllTokens();
      setTokenResult(res);
      toast({
        title: 'Tokens generados',
        description: `${res.created} nuevos, ${res.existing} ya existían`,
        status: 'success', duration: 4000,
      });
    } catch (e) {
      toast({ title: 'Error al generar tokens', description: e?.response?.data?.message ?? e.message, status: 'error', duration: 4000 });
    } finally {
      setGeneratingTokens(false);
    }
  };

  const openCreate = () => { setForm(EMPTY_FORM); setEditing(null); onOpenForm(); };
  const openEdit = (scanner) => {
    setForm({
      scanner_id: scanner.scanner_id, name: scanner.name,
      location: scanner.location ?? '', mode: scanner.mode,
      active: scanner.active,
      scheduled_end_time: scanner.config?.scheduled_end_time ?? '',
    });
    setEditing(scanner.scanner_id);
    onOpenForm();
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.scanner_id.trim()) {
      toast({ title: 'Nombre e ID son requeridos', status: 'warning', duration: 2500 });
      return;
    }
    setSaving(true);
    try {
      await upsertScanner({
        scanner_id: form.scanner_id.trim(), name: form.name.trim(),
        location: form.location.trim() || null, mode: form.mode, active: form.active,
        ...(form.mode === 'exit_log' ? { config: { scheduled_end_time: form.scheduled_end_time || null } } : {}),
      });
      toast({ title: editing ? 'Lector actualizado' : 'Lector creado', status: 'success', duration: 2500 });
      onCloseForm();
      fetchScanners();
    } catch (e) {
      toast({ title: 'Error al guardar', description: e?.response?.data?.errors ? JSON.stringify(e.response.data.errors) : e.message, status: 'error', duration: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (scanner) => { setDeleteTarget(scanner); onOpenDelete(); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(deleteTarget.scanner_id);
    try {
      await deleteScanner(deleteTarget.scanner_id);
      toast({ title: 'Lector eliminado', status: 'success', duration: 2500 });
      onCloseDelete();
      fetchScanners();
    } catch {
      toast({ title: 'Error al eliminar', status: 'error', duration: 3000 });
    } finally {
      setDeleting(null);
    }
  };

  const setF = (key) => (e) => setForm(f => ({
    ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  return (
    <Box fontFamily="jakarta">

      {/* ── Stats por modo ──────────────────────────────────────────────────── */}
      <SimpleGrid columns={{ base: 2, sm: 4 }} gap="14px" mb={5}>
        {MODES.map(m => {
          const count = scanners.filter(s => s.mode === m.value && s.active).length;
          return (
            <Flex
              key={m.value}
              bg="white" border="1px solid" borderColor="cream.line"
              borderRadius="16px" p={{ base: 3, md: 4 }} gap={{ base: '10px', md: '14px' }} align="center"
              boxShadow="0 2px 8px -2px rgba(35,30,26,.06)"
            >
              <Flex
                w={{ base: '34px', md: '42px' }} h={{ base: '34px', md: '42px' }} borderRadius="12px" bg={m.tileBg}
                align="center" justify="center" flexShrink={0}
              >
                <Icon as={m.icon} color={m.tileColor} boxSize={{ base: '16px', md: '21px' }} />
              </Flex>
              <Box minW={0}>
                <Text fontSize={{ base: '19px', md: '26px' }} fontWeight="800" color="ink.900" lineHeight={1} letterSpacing="-0.03em" noOfLines={1}>{count}</Text>
                <Text fontSize={{ base: '10.5px', md: '12px' }} color="ink.400" fontWeight="600" mt="4px" lineHeight="1.2" noOfLines={2}>{m.label}</Text>
              </Box>
            </Flex>
          );
        })}
      </SimpleGrid>

      {/* ── Cache offline card ───────────────────────────────────────────────── */}
      <Flex
        bg="sky.bg" border="1px solid" borderColor="#C8DEFA"
        borderRadius="16px" p={4} gap={4} align="center" mb={5}
        flexWrap={{ base: 'wrap', md: 'nowrap' }}
      >
        <Flex
          w="46px" h="46px" borderRadius="13px" bg="rgba(255,255,255,.7)"
          align="center" justify="center" flexShrink={0}
        >
          <Icon as={FiDatabase} color="sky.ink" boxSize="23px" />
        </Flex>
        <Box flex={1} minW={0}>
          <Text fontSize="15px" fontWeight="800" color="sky.ink">Caché sin conexión</Text>
          <Text fontSize="13px" color="#3A66B4" fontWeight="500" mt="3px">
            Genera los tokens QR de todos los alumnos para que el escáner funcione sin internet.
          </Text>
          {tokenResult && (
            <Text fontSize="12px" color="#3A66B4" fontWeight="600" mt="4px">
              Último resultado: {tokenResult.created} nuevos · {tokenResult.existing} ya existían · total {tokenResult.total}
            </Text>
          )}
        </Box>
        <Button
          h="40px" px={5} borderRadius="11px"
          bg="sky.ink" color="#fff" fontWeight="700" fontSize="13.5px"
          _hover={{ opacity: 0.9 }}
          leftIcon={<FiRefreshCw />}
          onClick={handleGenerateAllTokens}
          isLoading={generatingTokens}
          loadingText="Generando…"
          flexShrink={0}
          whiteSpace="nowrap"
        >
          Generar tokens para todos
        </Button>
      </Flex>

      {/* ── Encabezado sección ─────────────────────────────────────────────── */}
      <Flex align="center" gap="12px" mb={4} flexWrap="wrap">
        <Text fontSize="18px" fontWeight="800" color="ink.900" letterSpacing="-0.02em">
          Lectores configurados
        </Text>
        <Flex
          px="10px" py="4px" borderRadius="999px"
          bg="cream.bg2" border="1px solid" borderColor="cream.line"
          fontSize="12.5px" fontWeight="700" color="ink.400"
        >
          {scanners.filter(s => s.active).length} activos
        </Flex>
        <HStack ml="auto" spacing="8px" flexWrap="wrap" justify="flex-end">
          <Button
            h="38px" px={4} borderRadius="11px"
            border="1.5px solid" borderColor="cream.line2"
            bg="white" color="ink.700" fontWeight="700" fontSize="13px"
            _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
            leftIcon={<FiRefreshCw size={13} />}
            onClick={fetchScanners}
            isLoading={loading}
          >
            Actualizar
          </Button>
          <Button
            h="38px" px={4} borderRadius="11px"
            bg="coral.500" color="#fff" fontWeight="700" fontSize="13px"
            _hover={{ bg: 'coral.600' }}
            boxShadow="0 8px 18px -8px rgba(239,90,42,.65)"
            leftIcon={<FiPlus />}
            onClick={openCreate}
          >
            Nuevo lector
          </Button>
        </HStack>
      </Flex>

      {/* ── Tabla ───────────────────────────────────────────────────────────── */}
      <Box
        bg="white" border="1px solid" borderColor="cream.line"
        borderRadius="22px" overflow="hidden"
        boxShadow="0 2px 12px -4px rgba(35,30,26,.08)"
      >
        {error && (
          <Alert status="error" m={4} borderRadius="10px">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <VStack py={12} spacing={3}>
            <Spinner size="lg" color="coral.500" />
            <Text fontSize="13px" color="ink.400">Cargando lectores…</Text>
          </VStack>
        ) : scanners.length === 0 ? (
          <VStack py={12} spacing={3}>
            <Icon as={FiDatabase} color="ink.400" boxSize={8} />
            <Text color="ink.400" fontSize="13px">Sin lectores registrados</Text>
            <Button
              h="40px" px={5} borderRadius="11px"
              bg="coral.500" color="#fff" fontWeight="700"
              _hover={{ bg: 'coral.600' }}
              leftIcon={<FiPlus />}
              onClick={openCreate}
            >
              Crear primer lector
            </Button>
          </VStack>
        ) : (
          <>
            {/* Encabezado */}
            <Flex
              bg="cream.bg2" px={4} py="12px"
              borderBottom="1px solid" borderColor="cream.line"
              gap="12px"
            >
              <Text flex={2} fontSize="11.5px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em">Nombre</Text>
              <Text flex={1.5} fontSize="11.5px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em" display={{ base: 'none', md: 'block' }}>ID del lector</Text>
              <Text flex={1} fontSize="11.5px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em" display={{ base: 'none', lg: 'block' }}>Lugar</Text>
              <Text flex={1.5} fontSize="11.5px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em" display={{ base: 'none', sm: 'block' }}>Modo de operación</Text>
              <Text flex={1} fontSize="11.5px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em" display={{ base: 'none', sm: 'block' }}>H. salida prog.</Text>
              <Text flexShrink={0} minW="64px" fontSize="11.5px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em">Estado</Text>
              <Box w="72px" flexShrink={0} />
            </Flex>

            {scanners.map(scanner => (
              <Flex
                key={scanner.scanner_id}
                px={4} py="14px" gap="12px" align="center"
                borderTop="1px solid" borderColor="cream.line"
                _hover={{ bg: 'coral.tint' }} transition="background .13s"
                opacity={scanner.active ? 1 : 0.55}
              >
                <Box flex={2} minW={0}>
                  <Text fontWeight="700" fontSize="14.5px" color="ink.900" noOfLines={1}>
                    {scanner.name}
                  </Text>
                  <Box display={{ base: 'block', sm: 'none' }} mt="4px">
                    <ModeChip mode={scanner.mode} />
                  </Box>
                </Box>
                <Box flex={1.5} minW={0} display={{ base: 'none', md: 'block' }}>
                  <Flex
                    as="code" display="inline-block"
                    px="8px" py="3px" borderRadius="6px"
                    bg="cream.bg2" fontSize="12px" color="ink.700"
                  >
                    {scanner.scanner_id}
                  </Flex>
                </Box>
                <Box flex={1} minW={0} display={{ base: 'none', lg: 'block' }}>
                  <Flex align="center" gap="4px" color="ink.400">
                    <Icon as={FiMapPin} boxSize="13px" />
                    <Text fontSize="13px" fontWeight="600" noOfLines={1}>
                      {scanner.location ?? '—'}
                    </Text>
                  </Flex>
                </Box>
                <Box flex={1.5} minW={0} display={{ base: 'none', sm: 'block' }}>
                  <ModeChip mode={scanner.mode} />
                </Box>
                <Box flex={1} minW={0} display={{ base: 'none', sm: 'block' }}>
                  <Text fontSize="13px" fontWeight="700" color="ink.900">
                    {scanner.config?.scheduled_end_time ? `${scanner.config.scheduled_end_time} hrs` : '—'}
                  </Text>
                </Box>
                <Box flexShrink={0} minW="64px">
                  <Flex
                    px="10px" py="4px" borderRadius="999px" display="inline-flex"
                    bg={scanner.active ? '#DCF1E6' : 'cream.bg2'}
                    color={scanner.active ? '#1E7A4E' : 'ink.400'}
                    fontSize="12px" fontWeight="700"
                  >
                    {scanner.active ? 'Activo' : 'Inactivo'}
                  </Flex>
                </Box>
                <HStack spacing="6px" w="72px" justify="flex-end" flexShrink={0}>
                  <ActBtn icon={FiEdit3} label="Editar" onClick={() => openEdit(scanner)} />
                  <ActBtn icon={FiTrash2} label="Eliminar" danger onClick={() => confirmDelete(scanner)} isLoading={deleting === scanner.scanner_id} />
                </HStack>
              </Flex>
            ))}
          </>
        )}
      </Box>

      {/* ── Modal crear / editar ─────────────────────────────────────────────── */}
      <Modal isOpen={isFormOpen} onClose={onCloseForm} isCentered size="md">
        <ModalOverlay />
        <ModalContent mx={3}>
          <ModalHeader>{editing ? 'Editar lector' : 'Nuevo lector'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Nombre</FormLabel>
                <Input size="sm" borderRadius="8px" placeholder="Ej: Puerta Norte"
                  value={form.name} onChange={setF('name')} />
              </FormControl>
              <FormControl isRequired isDisabled={!!editing}>
                <FormLabel fontSize="sm">ID del lector</FormLabel>
                <Input size="sm" borderRadius="8px" placeholder="Ej: puerta-norte"
                  fontFamily="mono" value={form.scanner_id} onChange={setF('scanner_id')} />
                <FormHelperText fontSize="xs">
                  {editing ? 'El ID no se puede cambiar después de creado.' : 'Slug único, sin espacios ni tildes.'}
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Ubicación</FormLabel>
                <Input size="sm" borderRadius="8px" placeholder="Ej: Edificio A — Planta Baja"
                  value={form.location} onChange={setF('location')} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Modo de operación</FormLabel>
                <Select size="sm" borderRadius="8px" value={form.mode} onChange={setF('mode')}>
                  {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </Select>
              </FormControl>
              {form.mode === 'exit_log' && (
                <FormControl>
                  <FormLabel fontSize="sm">Hora de salida programada</FormLabel>
                  <Input type="time" size="sm" borderRadius="8px"
                    value={form.scheduled_end_time} onChange={setF('scheduled_end_time')} />
                  <FormHelperText fontSize="xs">
                    Si un alumno escanea antes de esta hora, se considerará salida anticipada.
                  </FormHelperText>
                </FormControl>
              )}
              <Divider />
              <FormControl display="flex" alignItems="center" gap={3}>
                <FormLabel mb={0} fontSize="sm">Activo</FormLabel>
                <Switch isChecked={form.active}
                  onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                  colorScheme="secondary" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" size="sm" onClick={onCloseForm}>Cancelar</Button>
            <Button variant="educational-primary" size="sm" onClick={handleSave} isLoading={saving}>
              {editing ? 'Actualizar' : 'Crear lector'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Modal eliminar ───────────────────────────────────────────────────── */}
      <Modal isOpen={isDeleteOpen} onClose={onCloseDelete} isCentered size="sm">
        <ModalOverlay />
        <ModalContent mx={3}>
          <ModalHeader>Eliminar lector</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack mb={3} spacing={3}>
              <Icon as={FiAlertTriangle} color="red.500" boxSize={6} />
              <Text fontWeight="semibold">¿Eliminar «{deleteTarget?.name}»?</Text>
            </HStack>
            <Text fontSize="sm" color="gray.500">
              Esta acción no se puede deshacer. El historial de accesos se conservará.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" size="sm" onClick={onCloseDelete}>Cancelar</Button>
            <Button colorScheme="red" size="sm" onClick={handleDelete} isLoading={!!deleting}>
              Sí, eliminar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ScannerAdmin;
